import { DateTime } from "luxon";
import { type SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useJobs } from "../../../hooks/useJobs";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useUpdateUserSettings } from "../../../hooks/useUserSettings";
import { getWeekIndexOfSpecificDate, useGetMasterScheduleWeeksAndActiveState } from "../../../pages/MasterSchedule/v2/useGetMasterScheduleWeeksAndActiveState";
import { localStorageService } from "../../../services/LocalStorageService";
import { getWeekStart } from "../../../utils/dateUtils";
import { isNurseActiveInDepartment } from "../../../utils/formatters";
import { getEnabledNurseJobs } from "../../../utils/jobs";
import {
	ChangesSource,
	type DepartmentLocation,
	type Job,
	type Nurse,
	NurseFullRole,
	NurseFullRoleShort,
	NurseRole,
	type RecurringShift,
	type RecurringShiftWithApplyFromDate,
	type UpdateMultipleRecurringShiftRequest,
	type UserSettings,
	type WeeklyRecurring,
} from "../../../utils/models";
import type { SystemAutoCompleteOption } from "../../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { useCreateOrUpdateMultipleRecurringShifts, useRecurringShiftPanelWeekStartDay } from "./hooks";
import {
	buildPrefilledShifts,
	generateNewShift,
	getRecurringShiftTabFromStorageKey,
	mergePerDayShifts,
	mergePerDayShiftsByWeek,
	RECURRING_SHIFT_TAB_STORAGE_KEYS,
	type RecurringShiftMasterScheduleProps,
	type RecurringShiftMasterScheduleStateCreateMode,
	type RecurringShiftMasterScheduleStateEditMode,
	RecurringShiftMasterScheduleType,
	RecurringShiftTabsTypes,
	type RecurringShiftWeekPrefill,
	type SingleRecurringShift,
	SingleRecurringShiftStatus,
	type WeeklyShifts,
	weekdayInSameWeek,
} from "./utils";

/**
 * Exported for tests only. It is where the per-day merge and the prefill append meet, and the order of
 * those two is load-bearing — see the comment above `mergedWeeksByIndexes`.
 */
export const getInitialRecurringShiftState = ({
	recurringShiftsData,
	clickedNurseId,
	clickedDate,
	masterScheduleWeeks,
	clickedLocation,
	timezone,
	firstSundayInDepartment,
	departmentLocations,
	getNurseById,
	previousState,
	shouldAddNewShiftOnOpen,
	staffShiftDefaultsSuggestion,
	isMedicalDepartment,
	enabledJobs,
	weekStartDay,
	isRuntimeWeekStartEnabled,
	isConsecutiveTabSelected,
	prefilledWeeks,
}: {
	shouldAddNewShiftOnOpen: boolean;
	clickedDate: DateTime;
	clickedNurseId?: number;
	recurringShiftsData?: RecurringShift[];
	masterScheduleWeeks: number;
	clickedLocation?: DepartmentLocation;
	timezone: string;
	firstSundayInDepartment: DateTime;
	departmentLocations: DepartmentLocation[];
	getNurseById: (id: number) => Nurse | undefined;
	previousState?: RecurringShiftMasterScheduleStateCreateMode | RecurringShiftMasterScheduleStateEditMode;
	staffShiftDefaultsSuggestion?: { startTime: string; endTime: string; location: string };
	isMedicalDepartment?: boolean;
	enabledJobs: Job[];
	weekStartDay: number;
	isRuntimeWeekStartEnabled: boolean;
	isConsecutiveTabSelected: boolean;
	prefilledWeeks?: RecurringShiftWeekPrefill[];
}): RecurringShiftMasterScheduleStateCreateMode | RecurringShiftMasterScheduleStateEditMode => {
	const baseState: RecurringShiftMasterScheduleStateCreateMode = {
		mode: RecurringShiftMasterScheduleType.CREATE,
		selectedNurseId: clickedNurseId ?? null,
		weeklyShifts: {
			shiftsThatRepeatEachWeek: [],
			weeksByIndexes: Array.from({ length: masterScheduleWeeks }, () => []),
		},
		existingShiftsToRemove: [],
		otherRecurringShifts: [],
	};
	// Anchor both ends to weekStartDay so non-Sunday views don't collapse biweek phases.
	const computeWeekIndex = (date: DateTime): number =>
		getWeekIndexOfSpecificDate({
			dateToCheck: date.setZone(timezone),
			masterScheduleWeeks,
			chosenDepartmentFirstSunday: firstSundayInDepartment,
			timezone,
			weekStartDay: isRuntimeWeekStartEnabled ? weekStartDay : 0,
		});
	const clickedDateWeekIndex = computeWeekIndex(clickedDate);

	const clickedNurse = clickedNurseId ? getNurseById(clickedNurseId) : undefined;
	const clickedNurseJobs = clickedNurse ? getEnabledNurseJobs(clickedNurse, enabledJobs) : [];
	const defaultNurseJobId = clickedNurseJobs.length === 1 ? clickedNurseJobs[0]?.id : undefined;
	const appendNewShift = shouldAddNewShiftOnOpen && !isConsecutiveTabSelected;
	/**
	 * The caller's proposed rows, one per template week (see `buildPrefilledShifts`). Built once here
	 * and merged into whichever of the two paths below runs, so the prefill survives the state rebuild
	 * that picking a staff member triggers — which is the whole point of the flow it exists for.
	 *
	 * Suppressed on the Other Recurring tab for the same reason `appendNewShift` is: a prefill
	 * describes weeks of the schedule template, and that tab is not showing them.
	 */
	const prefilled =
		prefilledWeeks && prefilledWeeks.length > 0 && !isConsecutiveTabSelected && !previousState
			? buildPrefilledShifts({
					prefilledWeeks,
					masterScheduleWeeks,
					clickedDateWeekIndex,
					clickedDateWeekStart: getWeekStart(clickedDate, weekStartDay),
					timezone,
					clickedLocation,
					staffShiftDefaultsSuggestion,
					isMedicalDepartment,
					defaultJobId: defaultNurseJobId,
				})
			: undefined;
	if (!recurringShiftsData || recurringShiftsData.length === 0 || !clickedNurseId) {
		const weeksByIndexes = baseState.weeklyShifts.weeksByIndexes;
		if (appendNewShift) {
			const newShift = generateNewShift({
				clickedDate: clickedDate,
				startDate: getWeekStart(clickedDate, weekStartDay),
				weeksInterval: masterScheduleWeeks,
				timezone,
				clickedLocation,
				staffShiftDefaultsSuggestion,
				isMedicalDepartment,
				jobId: defaultNurseJobId,
				consecutive: false,
			});
			weeksByIndexes[clickedDateWeekIndex].push(newShift);
		}
		return {
			...baseState,
			weeklyShifts: {
				// Spread FIRST, then override the two lists this merge is about: rebuilding the object from
				// named keys drops anything added to `weeklyShifts` later, and TypeScript would not catch it
				// — the result still satisfies the type because the named keys are present.
				...baseState.weeklyShifts,
				shiftsThatRepeatEachWeek: [...baseState.weeklyShifts.shiftsThatRepeatEachWeek, ...(prefilled?.shiftsThatRepeatEachWeek ?? [])],
				weeksByIndexes: weeksByIndexes.map((week, index) => [...week, ...(prefilled?.weeksByIndexes[index] ?? [])]),
			},
		};
	}

	const otherRecurringShifts: Array<SingleRecurringShift> = previousState?.otherRecurringShifts ?? [];
	const weeklyShiftThatRepeatEachWeek: WeeklyShifts =
		previousState?.weeklyShifts.shiftsThatRepeatEachWeek.filter((shift) => shift.status === SingleRecurringShiftStatus.NEW) ?? [];
	const calculatedWeeklyShifts: Array<WeeklyShifts> =
		previousState?.weeklyShifts.weeksByIndexes.map((week) => week.filter((shift) => shift.status === SingleRecurringShiftStatus.NEW)) ??
		Array.from({ length: masterScheduleWeeks }, () => []);
	const relevantRecurringShifts = clickedNurseId ? recurringShiftsData.filter((shift) => shift.nurseId === clickedNurseId) : [];
	if (relevantRecurringShifts.length > 0) {
		const relevantNurse = getNurseById(clickedNurseId);
		if (!relevantNurse) {
			//todo report errors?
		} else {
			const relevantRole = NurseRole[NurseFullRoleShort[relevantNurse.role]];
			for (const shift of relevantRecurringShifts) {
				const relevantLocations = Array.from(
					new Set(
						departmentLocations.filter(
							(departmentLocation) =>
								departmentLocation.location === (shift.location ?? "") &&
								(departmentLocation.roles.includes("all") || departmentLocation.roles.some((role) => role === NurseFullRole[relevantRole])),
						),
					),
				);
				if (relevantLocations.length === 0 && shift.location) {
					relevantLocations.push(...departmentLocations.filter((departmentLocation) => departmentLocation.location === shift.location));
				}
				const relevantLocationsByTime = relevantLocations.filter(
					(departmentLocation) =>
						departmentLocation.shift_start.substring(0, 5) === shift.shiftStart.substring(0, 5) &&
						departmentLocation.shift_end.substring(0, 5) === shift.shiftEnd.substring(0, 5),
				);
				const singleRelevantLocation = relevantLocationsByTime.at(0) ?? relevantLocations.at(0);
				const currentSingleShift: SingleRecurringShift = {
					startDate: DateTime.fromFormat(shift.startDate, "yyyy-MM-dd", { zone: timezone }),
					originalStartDate: DateTime.fromFormat(shift.startDate, "yyyy-MM-dd", { zone: timezone }),
					endDate: shift.endDate ? DateTime.fromFormat(shift.endDate, "yyyy-MM-dd", { zone: timezone }) : null,
					startTime: shift.shiftStart,
					endTime: shift.shiftEnd,
					recurrenceDetails: shift.recurringParams,
					location: singleRelevantLocation?.location,
					id: shift.id ?? -1,
					status: SingleRecurringShiftStatus.EXISTING,
					lastUIInteraction: null,
					jobId: shift.jobId,
				};
				if (shift.recurringParams.recurringType !== "Weekly" || shift.recurringParams.interval > masterScheduleWeeks) {
					otherRecurringShifts.push(currentSingleShift);
				} else {
					const weekIndex = computeWeekIndex(DateTime.fromFormat(shift.startDate, "yyyy-MM-dd", { zone: timezone }));
					if (weekIndex < 0 || weekIndex >= masterScheduleWeeks) {
						//todo report errors?
						continue;
					}
					if (shift.recurringParams.interval === 1) {
						weeklyShiftThatRepeatEachWeek.push({
							...currentSingleShift,
							isWeeklyShiftThatRepeatsEachWeek: true,
						});
					} else {
						calculatedWeeklyShifts[weekIndex].push(currentSingleShift);
					}
				}
			}
		}
	}

	if (appendNewShift && !previousState) {
		const newShift = generateNewShift({
			clickedDate: clickedDate,
			startDate: getWeekStart(clickedDate, weekStartDay),
			weeksInterval: masterScheduleWeeks,
			timezone,
			clickedLocation,
			staffShiftDefaultsSuggestion,
			isMedicalDepartment,
			jobId: defaultNurseJobId,
			consecutive: false,
		});
		calculatedWeeklyShifts[clickedDateWeekIndex].push(newShift);
	}

	// Appended AFTER the merge pass, not before: `mergePerDayShifts` only ever folds EXISTING per-day
	// DB rows together, and a prefilled row is NEW with several weekdays already on it — running it
	// through that pass would be a no-op at best and a mis-grouping at worst.
	const mergedWeeksByIndexes = !isRuntimeWeekStartEnabled
		? calculatedWeeklyShifts
		: mergePerDayShiftsByWeek(calculatedWeeklyShifts, weekStartDay, computeWeekIndex);
	const mergedRepeatEachWeek = !isRuntimeWeekStartEnabled ? weeklyShiftThatRepeatEachWeek : mergePerDayShifts(weeklyShiftThatRepeatEachWeek, weekStartDay);

	return {
		...baseState,
		mode: RecurringShiftMasterScheduleType.EDIT,
		selectedNurseId: clickedNurseId ?? null,
		weeklyShifts: {
			weeksByIndexes: mergedWeeksByIndexes.map((week, index) => [...week, ...(prefilled?.weeksByIndexes[index] ?? [])]),
			shiftsThatRepeatEachWeek: [...mergedRepeatEachWeek, ...(prefilled?.shiftsThatRepeatEachWeek ?? [])],
		},
		existingShiftsToRemove: previousState?.existingShiftsToRemove ?? [],
		otherRecurringShifts: !isRuntimeWeekStartEnabled ? otherRecurringShifts : mergePerDayShifts(otherRecurringShifts, weekStartDay),
	};
};

/**
 * Resolve which tab the modal opens on: an explicitly clicked shift's recurrence wins (master if its interval fits
 * the template window, otherwise Other Recurring); failing that, the user's persisted default; failing that, master.
 */
const resolveInitialTab = ({
	userSettings,
	knownClickedRecurringShiftId,
	recurringShiftsData,
	masterScheduleWeeks,
}: {
	userSettings: UserSettings | undefined;
	knownClickedRecurringShiftId?: number;
	recurringShiftsData?: RecurringShift[];
	masterScheduleWeeks: number;
}): RecurringShiftTabsTypes => {
	if (knownClickedRecurringShiftId) {
		const knownShift = recurringShiftsData?.find((shift) => shift.id === knownClickedRecurringShiftId);
		if (knownShift) {
			return knownShift.recurringParams.interval <= masterScheduleWeeks
				? RecurringShiftTabsTypes.masterScheduleShifts
				: RecurringShiftTabsTypes.otherRecurringShifts;
		}
	}
	return userSettings ? getRecurringShiftTabFromStorageKey(userSettings.recurring_shift_default_tab) : RecurringShiftTabsTypes.masterScheduleShifts;
};

export const useRecurringShiftMasterScheduleState = ({
	recurringShiftsData,
	userSettings,
	clickedNurseId,
	clickedLocation,
	clickedDate,
	onClose,
	shouldAddNewShiftOnOpen,
	knownClickedRecurringShiftId,
	prefilledWeeks,
	staffJobIdFilter,
}: RecurringShiftMasterScheduleProps & { userSettings: UserSettings | undefined }) => {
	const { trackError } = useAnalytics();
	const {
		userInfo: { id: userId },
	} = useAuth();
	const { chosenDepartment, timezone, hospitalData, getNurseById, calculateDefaultTimesAndLocationForStaff, isMedicalDepartment } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	const enabledJobs = useJobs();
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);
	const weekStartDay = useRecurringShiftPanelWeekStartDay();
	const { showSnackbar } = useSnackbar();
	const { mutate: updateUserSettings } = useUpdateUserSettings();

	const handleGetInitialRecurringShiftsData = useCallback(
		({
			relevantNurseId,
			addNewShiftOnOpen,
			isConsecutiveTabSelected,
		}: {
			relevantNurseId?: number;
			addNewShiftOnOpen: boolean;
			isConsecutiveTabSelected: boolean;
		}) => {
			// Calculate staff suggestion for new shifts if a nurse is selected
			const staffShiftDefaultsSuggestion = relevantNurseId && addNewShiftOnOpen ? calculateDefaultTimesAndLocationForStaff(relevantNurseId).at(0) : undefined;
			return getInitialRecurringShiftState({
				clickedNurseId: relevantNurseId,
				clickedDate,
				recurringShiftsData,
				masterScheduleWeeks: chosenDepartment.master_schedule_weeks,
				timezone,
				clickedLocation,
				firstSundayInDepartment: chosenDepartment.first_sunday,
				departmentLocations: chosenDepartment.locations,
				getNurseById,
				shouldAddNewShiftOnOpen: addNewShiftOnOpen,
				staffShiftDefaultsSuggestion,
				isMedicalDepartment,
				enabledJobs,
				weekStartDay,
				isRuntimeWeekStartEnabled,
				isConsecutiveTabSelected,
				prefilledWeeks,
			});
		},
		[
			clickedDate,
			recurringShiftsData,
			chosenDepartment,
			timezone,
			clickedLocation,
			getNurseById,
			calculateDefaultTimesAndLocationForStaff,
			isMedicalDepartment,
			enabledJobs,
			weekStartDay,
			isRuntimeWeekStartEnabled,
			prefilledWeeks,
		],
	);

	// The loader mounts this hook only once data + tab preference are ready, so initialization is fully synchronous.
	const [selectedTab, setSelectedTab] = useState<RecurringShiftTabsTypes>(() =>
		resolveInitialTab({ userSettings, knownClickedRecurringShiftId, recurringShiftsData, masterScheduleWeeks: chosenDepartment.master_schedule_weeks }),
	);
	const [recurringState, setRecurringState] = useState<RecurringShiftMasterScheduleStateCreateMode | RecurringShiftMasterScheduleStateEditMode>(() =>
		handleGetInitialRecurringShiftsData({
			relevantNurseId: clickedNurseId,
			addNewShiftOnOpen: shouldAddNewShiftOnOpen,
			isConsecutiveTabSelected: selectedTab === RecurringShiftTabsTypes.otherRecurringShifts,
		}),
	);

	/** Switch tab and persist the choice as the user's default for next time (personal setting, optimistic). */
	const applySelectedTab = useCallback(
		(tab: RecurringShiftTabsTypes) => {
			setSelectedTab(tab);
			updateUserSettings({ recurringShiftDefaultTab: RECURRING_SHIFT_TAB_STORAGE_KEYS[tab] });
		},
		[updateUserSettings],
	);
	const weeksAndActiveState = useGetMasterScheduleWeeksAndActiveState({ weekStartDay });

	const [warnOnClose, setWarnOnClose] = useState(false);

	const [previousRecurringState, setPreviousRecurringState] = useState([recurringState]);
	/**
	 * Only set to true when the user makes a change to the state, if we are auto adding a shift on open it's also a change.
	 */
	// A prefill seeds NEW rows the user did not type, so closing has to warn about discarding them —
	// same reason auto-adding a shift on open counts as a change.
	const [didPerformAnyChanges, setDidPerformAnyChanges] = useState(shouldAddNewShiftOnOpen || (prefilledWeeks !== undefined && prefilledWeeks.length > 0));
	useEffect(() => {
		// handle CTRL + Z functionality
		const handleKeyDown = (event: KeyboardEvent) => {
			if ((event.ctrlKey || event.metaKey) && event.key === "z") {
				localStorageService.setCtrlZTipUsed(true, userId);
				const lastChange = previousRecurringState.at(-1);
				if (lastChange) {
					setRecurringState(lastChange);
					setPreviousRecurringState((prev) => prev.slice(0, -1));
				}
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [previousRecurringState]);

	const handleSetRecurringState = useCallback(
		(newState: RecurringShiftMasterScheduleStateCreateMode | RecurringShiftMasterScheduleStateEditMode, ignoreDidPerformChange?: boolean) => {
			//keep track of last 3 states for undo functionality
			setPreviousRecurringState((prevState) => [...prevState.slice(-2), recurringState].filter((state) => state != null));
			if (!didPerformAnyChanges) {
				const currentTimeStamp = DateTime.now().setZone(timezone);
				const lastTimestamp = localStorageService.getLastCtrlZTipTimestamp(userId);
				const shouldAlert = (!lastTimestamp || currentTimeStamp.diff(lastTimestamp, "weeks").as("weeks") > 1) && !localStorageService.getCtrlZTipUsed(userId);
				if (shouldAlert) {
					showSnackbar("TIP: Press Ctrl + Z to undo changes", "info");
					localStorageService.setLastCtrlZTipTimestamp(currentTimeStamp, userId);
				}
				if (!ignoreDidPerformChange) {
					setDidPerformAnyChanges(true);
				}
			}
			setRecurringState(newState);
		},
		[recurringState, didPerformAnyChanges, timezone, showSnackbar, userId],
	);

	const handleSelectedNurseChange = useCallback(
		(e: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => {
			e.stopPropagation();
			handleSetRecurringState(
				handleGetInitialRecurringShiftsData({
					relevantNurseId: value?.value.id,
					addNewShiftOnOpen: shouldAddNewShiftOnOpen,
					isConsecutiveTabSelected: selectedTab === RecurringShiftTabsTypes.otherRecurringShifts,
				}),
				true,
			);
		},
		[handleSetRecurringState, handleGetInitialRecurringShiftsData, shouldAddNewShiftOnOpen, selectedTab],
	);

	const handleWeeklyShiftsChange = useCallback(
		(weekIndex: number, newShifts: WeeklyShifts, removedShiftToAppendToState?: SingleRecurringShift, shiftToRestoreFromRemoved?: SingleRecurringShift) => {
			const appended =
				removedShiftToAppendToState && removedShiftToAppendToState.status !== SingleRecurringShiftStatus.NEW
					? [...recurringState.existingShiftsToRemove, removedShiftToAppendToState]
					: recurringState.existingShiftsToRemove;
			const filteredAppended = shiftToRestoreFromRemoved ? appended.filter((shift) => shift.id !== shiftToRestoreFromRemoved.id) : appended;

			handleSetRecurringState({
				...recurringState,
				weeklyShifts: {
					...recurringState.weeklyShifts,
					weeksByIndexes: recurringState.weeklyShifts.weeksByIndexes.map((shifts, index) => (index === weekIndex ? newShifts : shifts)),
				},
				existingShiftsToRemove: filteredAppended,
			});
		},
		[handleSetRecurringState, recurringState],
	);

	const handleOtherRecurrenceShiftsChange = useCallback(
		(newShifts: Array<SingleRecurringShift>, removedShiftToAppendToState?: SingleRecurringShift, shiftToRestoreFromRemoved?: SingleRecurringShift) => {
			const appended =
				removedShiftToAppendToState && removedShiftToAppendToState.status !== SingleRecurringShiftStatus.NEW
					? [...recurringState.existingShiftsToRemove, removedShiftToAppendToState]
					: recurringState.existingShiftsToRemove;
			const filteredAppended = shiftToRestoreFromRemoved ? appended.filter((shift) => shift.id !== shiftToRestoreFromRemoved.id) : appended;

			handleSetRecurringState({
				...recurringState,
				otherRecurringShifts: newShifts,
				existingShiftsToRemove: filteredAppended,
			});
		},
		[handleSetRecurringState, recurringState],
	);

	const handleShiftsThatRepeatEachWeekChange = useCallback(
		(newShifts: WeeklyShifts, removedShiftToAppendToState?: SingleRecurringShift, shiftToRestoreFromRemoved?: SingleRecurringShift) => {
			const appended =
				removedShiftToAppendToState && removedShiftToAppendToState.status !== SingleRecurringShiftStatus.NEW
					? [...recurringState.existingShiftsToRemove, removedShiftToAppendToState]
					: recurringState.existingShiftsToRemove;
			const filteredAppended = shiftToRestoreFromRemoved ? appended.filter((shift) => shift.id !== shiftToRestoreFromRemoved.id) : appended;

			handleSetRecurringState({
				...recurringState,
				weeklyShifts: {
					...recurringState.weeklyShifts,
					shiftsThatRepeatEachWeek: newShifts,
				},
				existingShiftsToRemove: filteredAppended,
			});
		},
		[handleSetRecurringState, recurringState],
	);

	const handleCloseInitiated = useCallback(() => {
		if (didPerformAnyChanges) {
			setWarnOnClose(true);
		} else {
			onClose?.();
		}
	}, [didPerformAnyChanges, onClose]);
	const handleCloseWarnCancel = useCallback(() => {
		setWarnOnClose(false);
	}, []);
	const handleCloseWarnConfirm = useCallback(() => {
		setWarnOnClose(false);
		onClose?.();
	}, [onClose]);

	const nursesList = useMemo(
		() =>
			hospitalData.nurses
				.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id))
				// Scoped to the job when the caller has one — see `staffJobIdFilter`. Falsy, not `=== undefined`:
				// an empty string is "no job yet", and `jobIds.includes("")` matches nobody, so the strict
				// check would hand back an empty staff list with nothing to explain it. A filter fails open.
				// A nurse carries every
				// job they can work, so this is "could take this shift", not "usually works it".
				.filter((nurse) => !staffJobIdFilter || (nurse.jobIds ?? []).includes(staffJobIdFilter))
				.map<SystemAutoCompleteOption<Nurse>>((nurse) => ({
					value: nurse,
					label: formatPersonName(nurse),
					disabled: !!nurse.scheduledDeactivationDate && nurse.scheduledDeactivationDate <= clickedDate.startOf("day"),
				})),
		[hospitalData.nurses, clickedDate, chosenDepartment.department_id, staffJobIdFilter, formatPersonName],
	);
	const currentlySelectedNurse = useMemo((): SystemAutoCompleteOption<Nurse> | null => {
		const currentNurse = recurringState.selectedNurseId ? getNurseById(recurringState.selectedNurseId) : undefined;
		if (!currentNurse) {
			return null;
		}
		return {
			value: currentNurse,
			label: formatPersonName(currentNurse),
		};
	}, [getNurseById, recurringState.selectedNurseId, formatPersonName]);

	const countWeeklyRecurrenceDaysAmount = useCallback(
		(singleRecurringShiftArray: SingleRecurringShift[]) =>
			singleRecurringShiftArray
				.filter((shift) => !recurringState.existingShiftsToRemove.some((shiftToRemove) => shiftToRemove.id === shift.id))
				.reduce(
					(sum: number, currentShift: SingleRecurringShift) =>
						sum + (currentShift.recurrenceDetails.recurringType === "Weekly" ? currentShift.recurrenceDetails.weekdays.length : 0),
					0,
				),
		[recurringState.existingShiftsToRemove],
	);

	const countConsecutiveRecurrenceDaysAmount = useCallback(
		(singleRecurringShiftArray: SingleRecurringShift[]) =>
			singleRecurringShiftArray
				.filter((shift) => !recurringState.existingShiftsToRemove.some((shiftToRemove) => shiftToRemove.id === shift.id))
				.reduce(
					(sum: number, currentShift: SingleRecurringShift) =>
						sum + (currentShift.recurrenceDetails.recurringType === "ConsecutiveDays" ? currentShift.recurrenceDetails.working_days_amount : 0),
					0,
				),
		[recurringState.existingShiftsToRemove],
	);

	const amountOfShiftsWeekly = useMemo(
		() => countWeeklyRecurrenceDaysAmount(recurringState.weeklyShifts.shiftsThatRepeatEachWeek) * chosenDepartment.master_schedule_weeks,
		[recurringState, countWeeklyRecurrenceDaysAmount, chosenDepartment.master_schedule_weeks],
	);
	const amountOfShiftsInterval = useMemo(
		() =>
			recurringState.weeklyShifts.weeksByIndexes
				.filter((week) => week.length > 0)
				.reduce((total, currentWeek) => total + countWeeklyRecurrenceDaysAmount(currentWeek), 0),
		[recurringState, countWeeklyRecurrenceDaysAmount],
	);
	const amountOfOtherRecurringShifts = useMemo(
		() => countWeeklyRecurrenceDaysAmount(recurringState.otherRecurringShifts) + countConsecutiveRecurrenceDaysAmount(recurringState.otherRecurringShifts),
		[recurringState, countWeeklyRecurrenceDaysAmount, countConsecutiveRecurrenceDaysAmount],
	);

	const { mutateAsync: performUpdate, isPending: isUpdateLoading } = useCreateOrUpdateMultipleRecurringShifts();

	const singleRecurringShiftToRecurringShift = useCallback(
		(shift: SingleRecurringShift): { recurringShift: RecurringShift; applyFromDate: string } | null =>
			recurringState.selectedNurseId
				? {
						recurringShift: {
							id: typeof shift.id === "number" && shift.id > 0 ? shift.id : undefined,
							nurseId: recurringState.selectedNurseId,
							shiftStart: shift.startTime,
							shiftEnd: shift.endTime,
							location: shift.location,
							startDate: (shift.originalStartDate ?? shift.startDate).toFormat("yyyy-MM-dd"),
							endDate: shift.endDate ? shift.endDate.toFormat("yyyy-MM-dd") : undefined,
							deleted: false,
							departmentId: chosenDepartment.department_id,
							recurringParams: shift.recurrenceDetails,
							jobId: shift.jobId,
						},
						applyFromDate: shift.startDate.toFormat("yyyy-MM-dd"),
					}
				: null,
		[recurringState.selectedNurseId, chosenDepartment.department_id],
	);

	const expandShiftToOps = useCallback(
		(shift: SingleRecurringShift): { creates: RecurringShift[]; updates: RecurringShiftWithApplyFromDate[]; removes: RecurringShiftWithApplyFromDate[] } => {
			const empty = { creates: [] as RecurringShift[], updates: [] as RecurringShiftWithApplyFromDate[], removes: [] as RecurringShiftWithApplyFromDate[] };
			if (!recurringState.selectedNurseId) return empty;
			const merged = shift.originalRowsByWeekday;
			const isMerged = !!merged && Object.keys(merged).length > 0;
			const isWeekly = shift.recurrenceDetails.recurringType === "Weekly";

			// True when a "merged" shift whose merged entries (days) are all backed by the same DB row (legacy schema).
			const isLegacyParent = isMerged && Object.keys(merged ?? {}).length > 1 && new Set(Object.values(merged ?? {}).map((m) => m.id)).size === 1;
			if (!isRuntimeWeekStartEnabled || !isMerged || !isWeekly || isLegacyParent) {
				const mapped = singleRecurringShiftToRecurringShift(shift);
				if (!mapped) return empty;
				if (shift.status === SingleRecurringShiftStatus.NEW) {
					const weekly = shift.recurrenceDetails;
					// Weekday count is deliberately not a condition here: a one-weekday row needs the
					// anchoring below just as much as a many-weekday one, since startDate arrives as the
					// displayed week's first day rather than the day the row recurs on.
					if (!isRuntimeWeekStartEnabled || weekly.recurringType !== "Weekly") {
						return { ...empty, creates: [mapped.recurringShift] };
					}
					// Per-day rows: anchor each row's startDate to its own first fire so the BE
					// biweek-phase math (start_date's Sunday) lines up with the user's intent.
					const creates = weekly.weekdays.map((weekday) => ({
						...mapped.recurringShift,
						startDate: weekdayInSameWeek(shift.startDate, weekday, weekStartDay),
						recurringParams: { ...weekly, weekdays: [weekday] },
					}));
					return { ...empty, creates };
				}
				if (shift.status === SingleRecurringShiftStatus.EDITED) {
					return { ...empty, updates: [{ recurringShift: mapped.recurringShift, applyFromDate: mapped.applyFromDate }] };
				}
				if (shift.status === SingleRecurringShiftStatus.TO_DELETE) {
					return { ...empty, removes: [{ recurringShift: mapped.recurringShift, applyFromDate: mapped.applyFromDate }] };
				}
				return empty;
			}

			const weeklyParams = shift.recurrenceDetails as WeeklyRecurring;
			const mapped = singleRecurringShiftToRecurringShift(shift);
			if (!mapped) return empty;
			const baseRow = mapped.recurringShift;

			if (shift.status === SingleRecurringShiftStatus.TO_DELETE) {
				const removes = Object.entries(merged).map<RecurringShiftWithApplyFromDate>(([weekday, original]) => ({
					recurringShift: { ...baseRow, id: original.id, startDate: original.startDate, recurringParams: { ...weeklyParams, weekdays: [weekday] } },
					applyFromDate: original.startDate,
				}));
				return { ...empty, removes };
			}

			if (shift.status === SingleRecurringShiftStatus.EXISTING) return empty;

			// At this point we are looking at a truly merged, new-model, weekly shift that the user has edited (status === EDITED)
			// Diff the user's current weekday selection against the originally-merged per-day rows:
			// kept weekdays → UPDATE the existing per-day row; newly added → CREATE; removed → REMOVE.
			// Each op targets a single weekday so the backend keeps one DB row per weekday.
			const currentWeekdays = weeklyParams.weekdays.map((w) => w.toLowerCase());
			const currentSet = new Set(currentWeekdays);
			const userChangedApplyFrom = !shift.startDate.equals(shift.originalStartDate ?? shift.startDate);
			const creates: RecurringShift[] = [];
			const updates: RecurringShiftWithApplyFromDate[] = [];
			const removes: RecurringShiftWithApplyFromDate[] = [];
			for (const weekday of currentWeekdays) {
				// Keep all per-day rows in the same week as shift.startDate so they share one biweek phase.
				const perDayStartDate = weekdayInSameWeek(shift.startDate, weekday, weekStartDay);
				const perDayParams: WeeklyRecurring = { ...weeklyParams, weekdays: [weekday] };
				const original = merged[weekday];
				if (original) {
					updates.push({
						recurringShift: {
							...baseRow,
							id: original.id,
							startDate: userChangedApplyFrom ? perDayStartDate : original.startDate,
							recurringParams: perDayParams,
						},
						// Sweep the old range too so shifts between the old and new start get diffed away.
						applyFromDate: original.startDate,
					});
				} else {
					creates.push({ ...baseRow, id: undefined, startDate: perDayStartDate, recurringParams: perDayParams });
				}
			}
			for (const weekday of Object.keys(merged)) {
				if (currentSet.has(weekday)) continue;
				const original = merged[weekday];
				removes.push({
					recurringShift: { ...baseRow, id: original.id, startDate: original.startDate, recurringParams: { ...weeklyParams, weekdays: [weekday] } },
					applyFromDate: original.startDate,
				});
			}
			return { creates, updates, removes };
		},
		[recurringState.selectedNurseId, singleRecurringShiftToRecurringShift, isRuntimeWeekStartEnabled, weekStartDay],
	);

	const convertStateToRequest = useCallback((): UpdateMultipleRecurringShiftRequest => {
		const newShiftsToCreate: RecurringShift[] = [];
		const existingShiftsToUpdate: RecurringShiftWithApplyFromDate[] = [];
		const shiftsToRemove: RecurringShiftWithApplyFromDate[] = [];
		const allShifts: SingleRecurringShift[] = [
			...recurringState.weeklyShifts.weeksByIndexes.flat(),
			...recurringState.weeklyShifts.shiftsThatRepeatEachWeek,
			...recurringState.otherRecurringShifts,
			...recurringState.existingShiftsToRemove.map((s) => ({ ...s, status: SingleRecurringShiftStatus.TO_DELETE })),
		];
		for (const shift of allShifts) {
			const ops = expandShiftToOps(shift);
			newShiftsToCreate.push(...ops.creates);
			existingShiftsToUpdate.push(...ops.updates);
			shiftsToRemove.push(...ops.removes);
		}
		return {
			departmentId: chosenDepartment.department_id,
			source: ChangesSource.MasterSchedule,
			shiftsToRemove,
			existingRecurringShifts: existingShiftsToUpdate,
			newRecurringShifts: newShiftsToCreate,
		};
	}, [recurringState, chosenDepartment.department_id, expandShiftToOps]);

	const handleSaveConfirm = useCallback(async () => {
		const request = convertStateToRequest();
		if (request.existingRecurringShifts.length === 0 && request.newRecurringShifts.length === 0 && request.shiftsToRemove.length === 0) {
			showSnackbar("No changes to save", "info");
			return;
		}
		const result = await performUpdate({ ...request, nurseId: recurringState.selectedNurseId ?? undefined });
		if (result?.status === 200 || result?.status === 201) {
			if (result.data.allSucceeded) {
				onClose?.(true);
			} else {
				trackError("RecurringShiftMasterSchedule_UpdateFailed", {
					request: JSON.stringify(request),
					responseStatus: result?.status,
				});
			}
		} else {
			trackError("RecurringShiftMasterSchedule_UpdateFailed", {
				request: JSON.stringify(request),
				responseStatus: result?.status,
			});
		}
	}, [performUpdate, convertStateToRequest, onClose, trackError, showSnackbar]);

	const totalShiftAmount = amountOfOtherRecurringShifts + amountOfShiftsInterval + amountOfShiftsWeekly;

	const hasShiftsWithEmptyDays = useMemo(() => {
		const removedIds = new Set(recurringState.existingShiftsToRemove.map((s) => s.id));
		const allShifts = [
			...recurringState.weeklyShifts.weeksByIndexes.flat(),
			...recurringState.weeklyShifts.shiftsThatRepeatEachWeek,
			...recurringState.otherRecurringShifts,
		];
		return allShifts.some(
			(shift) => !removedIds.has(shift.id) && shift.recurrenceDetails.recurringType === "Weekly" && shift.recurrenceDetails.weekdays.length === 0,
		);
	}, [recurringState]);

	const hasPendingRequestChanges = useMemo(() => {
		const request = convertStateToRequest();
		return request.newRecurringShifts.length > 0 || request.existingRecurringShifts.length > 0 || request.shiftsToRemove.length > 0;
	}, [convertStateToRequest]);

	const saveButtonEnabled = hasPendingRequestChanges && !!recurringState.selectedNurseId;

	const [warnTabChangeUnsavedChanges, setWarnTabChangeUnsavedChanges] = useState</**
	 We pass the tab we want to switch to or null if we dont need a warning*/ RecurringShiftTabsTypes | null>(null);

	const handleTabChangeConfirm = useCallback(() => {
		/**
		 * We discard any unsaved changes when changing tabs after confirming the warning.
		 */
		if (warnTabChangeUnsavedChanges) {
			applySelectedTab(warnTabChangeUnsavedChanges);
			setWarnTabChangeUnsavedChanges(null);
			setDidPerformAnyChanges(false);
			setRecurringState(
				handleGetInitialRecurringShiftsData({
					/*Take the current nurse ID or the initially passed one*/
					relevantNurseId: recurringState.selectedNurseId ?? clickedNurseId,
					addNewShiftOnOpen: false,
					// The tab we are switching to drives this — `selectedTab` is still the old tab here (applySelectedTab hasn't propagated).
					isConsecutiveTabSelected: warnTabChangeUnsavedChanges === RecurringShiftTabsTypes.otherRecurringShifts,
				}),
			);
		}
	}, [recurringState, warnTabChangeUnsavedChanges, handleGetInitialRecurringShiftsData, clickedNurseId, applySelectedTab]);

	const handleTabChangeCancel = useCallback(() => {
		setWarnTabChangeUnsavedChanges(null);
	}, []);

	const onTabChange = useCallback(
		(tab: RecurringShiftTabsTypes) => {
			if (saveButtonEnabled) {
				setWarnTabChangeUnsavedChanges(tab);
				return;
			}
			applySelectedTab(tab);
		},
		[saveButtonEnabled, applySelectedTab],
	);

	return {
		selectedNurseId: recurringState.selectedNurseId,
		recurringState,
		weeksAndActiveState,
		nursesList,
		currentlySelectedNurse,
		handleSelectedNurseChange,
		handleWeeklyShiftsChange,
		handleShiftsThatRepeatEachWeekChange,
		amountOfShiftsWeekly,
		amountOfShiftsInterval,
		handleCloseInitiated,
		warnOnClose,
		handleCloseWarnCancel,
		handleCloseWarnConfirm,
		isUpdateLoading,
		handleSaveConfirm,
		selectedTab,
		onTabChange,
		handleOtherRecurrenceShiftsChange,
		amountOfOtherRecurringShifts,
		totalShiftAmount,
		saveButtonEnabled,
		warnTabChangeUnsavedChanges,
		handleTabChangeConfirm,
		handleTabChangeCancel,
		hasShiftsWithEmptyDays,
		weekStartDay,
	};
};
